import i18n from "@/i18n";
import dayjs from "dayjs";
import hexRgb from "hex-rgb";
import Color from "color";
import duration from "dayjs/plugin/duration";
import 'dayjs/locale/el';
import 'dayjs/locale/en';
import { clientsModule, partnersModule } from "@/store/modules/store-accessor";

dayjs.extend(duration);

function formatDate(dateTime: string) {
  if (dateTime !== null && dateTime !== undefined) {
    return dayjs(dateTime).format("MMM DD, YYYY HH:mm:ss");
  } else {
    return "-";
  }
}

function formatDateCalendar(dateTime: Date) {
  if (dateTime !== null && dateTime !== undefined) {
    return dayjs(dateTime).format("YYYY-MM-DD HH:mm");
  } else {
    return "-";
  }
}

function formatOnlyDate(dateTime: Date) {
  if (dateTime !== null && dateTime !== undefined) {
    return dayjs(dateTime).locale(localStorage.getItem('lang') || 'el').format("MMM DD, YYYY");
  } else {
    return "";
  }
}

function formatDateWithoutSeconds(dateTime: Date) {
  if (dateTime !== null && dateTime !== undefined) {
    return dayjs(dateTime).locale(localStorage.getItem('lang') || 'el').format("MMM DD, YYYY HH:mm");
  } else {
    return "";
  }
}

function convertMinsToHrsMins(mins: number) {
  const hours = mins / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  if (rhours == 0) {
    if (rminutes < 10) {
      return rminutes + "m";
    } else {
      return ("0" + rminutes).slice(-2) + "m";
    }
  } else if (rminutes == 0) {
    return rhours + "h";
  } else {
    return rhours + "h " + ("0" + rminutes).slice(-2) + "m";
  }
}

function calcDuration(start: Date, end: Date) {
  if (
    start !== null ||
    start !== undefined ||
    end !== null ||
    end !== undefined
  ) {
    const newStart = dayjs(start);
    const newEnd = dayjs(end);

    const duration = dayjs.duration(newEnd.diff(newStart));

    const durationHoursMinutes = convertMinsToHrsMins(duration.asMinutes());

    return durationHoursMinutes;
  } else {
    return "-";
  }
}

function mergedAddressAndArea(address: string, area: string) {
  return address || area ? [address, area].filter(Boolean).join(", ") : "-";
}

function appointmentStatusToName(status: string) {
  if (status == "1") return "appointmentCanceled";
  else if (status == "2") return "appointmentPending";
  else if (status == "3") return "appointmentOngoing";
  else if (status == "4") return "appointmentComplete";
  else if (status == "5") return "appointmentInComplete";
  else return "appointmentPending";
}

function appointmentStatusToColor(status: string) {
  if (status == "1") return "appointmentStatusError";
  else if (status == "2") return "appointmentTypeError";
  else if (status == "3") return "info";
  else if (status == "4") return "success";
  else if (status == "5") return "appointmentInCompleteColor";
  else return "appointmentTypeError";
}

function addOpacity(color: any, opacity: number): string {
  try {
    const parsedColor = Color(color).alpha(opacity);
    return parsedColor.hexa().toUpperCase();
  } catch (err) {
    return color;
  }
}

function roundMinutes(minutes: number) {
  if (minutes >= 59 || minutes === 0) return "00";
  minutes = Math.ceil(minutes / 15) * 15 - 15;
  if (minutes < 10) return `0${minutes}`;
  else return minutes.toString();
}

function sortDateRange(dateRange: any) {
  const startDate = dateRange[0];
  const endDate = dateRange[1];

  const startDateConv = new Date(startDate);
  const endDateConv = new Date(endDate);

  if (startDateConv <= endDateConv) {
    dateRange[0] = startDate;
    dateRange[1] = endDate;
  } else {
    dateRange[0] = endDate;
    dateRange[1] = startDate;
  }
}

function formatDatesRangeText(startDate: any, endDate: any) {
  if (!startDate) return null;

  const dateArray = [dayjs(startDate)];

  if (endDate) {
    dateArray.push(dayjs(endDate));
  }

  dateArray.sort((a, b) => (a.isAfter(b) ? 1 : -1));

  const formattedDates = dateArray.map((date) => date.format("DD/MM/YYYY"));

  if (!endDate) {
    formattedDates.push("");
  }

  return formattedDates.join(" - ");
}

function primaryContactExists(model: any) {
  let exists = false;
  model.contacts.forEach((contact: any) => {
    if (contact.is_primary == true) {
      exists = true;
    }
  });
  return exists;
}

function statusCodeMsg(statusCode: string) {
  const STATUS_CODES: any = {
    "400": "400_BAD_REQUEST_ERROR_API",
    "401": "401_UNAUTHORIZED_ERROR_API",
    "403": "403_FORBIDDEN_ERROR_API",
    "404": "404_NOT_FOUND_ERROR_API",
    "406": "406_NOT_ACCEPTED_ERROR_API",
    "409": "409_CONFLICT_ERROR_API",

    "500": "500_INTERNAL_SERVER_ERROR_API",
    "502": "502_BAD_GATEWAY_ERROR_API",
    "503": "503_SERVICE_UNAVAILABLE_API",
  };

  if (STATUS_CODES[statusCode]) {
    return `${i18n.t(STATUS_CODES[statusCode])} ${statusCode}`;
  }

  return "API_GENERAL_ERROR";
}

function taskStatusToName(status: number) {
  if (status == 1) return "taskCanceled";
  else if (status == 2) return "taskPending";
  else if (status == 3) return "taskCompleted";
  else if (status == 4) return "taskCompletedWithAppointment";
  else if (status == 5) return "taskCompletedWithJob";
}

function taskStatusToColor(status: number) {
  if (status == 1) return "taskStatusError";
  else if (status == 2) return "taskTypeError";
  else if (status == 3) return "success";
  else if (status == 4) return "taskCompletedWithAppointmentColor";
  else if (status == 5) return "taskCompletedWithJobColor";
  else return "taskTypeError";
}

function formatAmount(amount: number) {
  if (amount === undefined) {
    return 0;
  } else if (amount - Math.floor(amount) !== 0) {
    return (Math.round(amount * 100) / 100).toFixed(2);
  } else {
    return amount;
  }
}

function customSearch(value: any, search: any, item: any) {
  return Object.values(item).some(
    (v: any) =>
      v &&
      v
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replaceAll("a", "α")
        .replaceAll("b", "β")
        .replaceAll("c", "ψ")
        .replaceAll("d", "δ")
        .replaceAll("e", "ε")
        .replaceAll("f", "φ")
        .replaceAll("g", "γ")
        .replaceAll("h", "η")
        .replaceAll("i", "ι")
        .replaceAll("j", "ξ")
        .replaceAll("k", "κ")
        .replaceAll("l", "λ")
        .replaceAll("m", "μ")
        .replaceAll("n", "ν")
        .replaceAll("o", "ο")
        .replaceAll("p", "π")
        .replaceAll("r", "ρ")
        .replaceAll("s", "σ")
        .replaceAll("t", "τ")
        .replaceAll("u", "υ")
        .replaceAll("v", "ν")
        .replaceAll("w", "ω")
        .replaceAll("x", "χ")
        .replaceAll("y", "υ")
        .replaceAll("z", "ζ")
        .replaceAll("ω", "ο")
        .replaceAll("ς", "σ")
        .includes(
          search
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replaceAll("a", "α")
            .replaceAll("b", "β")
            .replaceAll("c", "ψ")
            .replaceAll("d", "δ")
            .replaceAll("e", "ε")
            .replaceAll("f", "φ")
            .replaceAll("g", "γ")
            .replaceAll("h", "η")
            .replaceAll("i", "ι")
            .replaceAll("j", "ξ")
            .replaceAll("k", "κ")
            .replaceAll("l", "λ")
            .replaceAll("m", "μ")
            .replaceAll("n", "ν")
            .replaceAll("o", "ο")
            .replaceAll("p", "π")
            .replaceAll("r", "ρ")
            .replaceAll("s", "σ")
            .replaceAll("t", "τ")
            .replaceAll("u", "υ")
            .replaceAll("v", "ν")
            .replaceAll("w", "ω")
            .replaceAll("x", "χ")
            .replaceAll("y", "υ")
            .replaceAll("z", "ζ")
            .replaceAll("ω", "ο")
            .replaceAll("ς", "σ")
        )
  );
}

function randomHexColor() {
  const randomHexColor = Math.floor(Math.random() * 16777215);
  const hexColor = randomHexColor.toString(16).padStart(6, "0");

  return `#${hexColor}`;
}

function hexColorRemoveLastTwoDigits(hexColor: string) {
  if (hexColor.length === 9 && hexColor.toUpperCase().slice(7) === "FF") {
    return hexColor.slice(0, 7);
  } else {
    return hexColor;
  }
}

function entryIcon(action: any) {
  switch (action.entry_type) {
    case "action":
      return "mdi-account-wrench-outline";
    case "payment":
      return getPaymentIcon(action.payment_type_id);
    case "material":
      return "mdi-hammer-screwdriver";
    case "expense":
      return "mdi-cash-off";
    default:
      return "";
  }
}

function getOptimalTextColor(hexColorString: string) {
  const { red, green, blue, alpha } = hexRgb(hexColorString);

  //Commented is the 2nd option that complies with w3c standards for accessibility
  //We selected the 1st option because it is not aggressive and select white text more frequently
  // const uicolors = [red / 255, green / 255, blue / 255];
  // const c = uicolors.map((col) => {
  //   if (col <= 0.03928) {
  //     return col / 12.92;
  //   }
  //   return Math.pow((col + 0.055) / 1.055, 2.4);
  // });
  // const L = (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2]);
  // return (L > 0.179) ? 'black' : 'white';
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186 ? "black" : "white";
}

function sortTableDates(a: string, b: string) {
  const dateA: any = new Date(a);
  const dateB: any = new Date(b);
  return dateA - dateB;
}

function isDecimalNumber(evt: any) {
  // Prevent user from entering negative values (-1) and characters
  const theEvent = evt || window.event;
  let key = theEvent.keyCode || theEvent.which;
  key = String.fromCharCode(key);
  if (key.length == 0) return;
  const regex = /^[0-9.,\b]+$/;
  if (!regex.test(key)) {
    theEvent.returnValue = false;
    if (theEvent.preventDefault) theEvent.preventDefault();
  }
}

function taskColor(task: Record<string, any>) {
  if (!task.due_at || [3, 4, 5].includes(task.task_status_id)) {
    return 'textGrey';
  } else if (!task.all_day && dayjs(task.due_at).isSame(dayjs(), 'day') && dayjs(task.due_at).isAfter(dayjs())) {
    return 'darkGreen';
  } else if (task.all_day && dayjs(task.due_at).isSame(dayjs(), 'day')) {
    return 'darkGreen';
  } else if (dayjs(task.due_at).isAfter(dayjs())) {
    return 'futureTask';
  } else {
    return 'pastTask';
  }
}

async function setGoogleMapsInfoToModel(
  modelName: Record<string, any>,
  latitude: any,
  longitude: any
) {
  let lang = localStorage.getItem("lang") || "el";
  if (lang === "gr") {
    lang = "el";
  }

  try {
    const googleKey =
      process.env.NODE_ENV === "production"
        ? "GOOGLE_MAPS_KEY"
        : "AIzaSyDbgvgejSNtTI410Z1ihbNGl-0iEpfPl0k";

    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=${lang}&key=${googleKey}`;
    const response = await fetch(apiUrl);
    const responseData = await response.json();

    if (responseData?.results?.length > 0) {
      const result = responseData.results[0];
      const address = result.formatted_address || "";
      let area = "";
      let zipCode = "";

      for (const component of result.address_components) {
        if (
          component.types.includes("locality") ||
          component.types.includes("political")
        ) {
          area = component.long_name;
        }
        if (component.types.includes("postal_code")) {
          zipCode = component.long_name.replace(/ /g, "");
        }
      }

      modelName.address = address;
      modelName.area = area;
      modelName.latitude = latitude?.toFixed(7);
      modelName.longitude = longitude?.toFixed(7);
      modelName.zip_code = zipCode;
    } else {
      modelName.latitude = latitude?.toFixed(7);
      modelName.longitude = longitude?.toFixed(7);
    }
  } catch (error) {
    console.error("Error fetching data from Google Maps API");
    return 0;
  }
}

async function getGoogleMapsInfo(latitude: any, longitude: any) {
  const lang = localStorage.getItem('lang') || 'el';

  const mapModel = {
    address: '',
    area: '',
    latitude,
    longitude,
    zip_code: null as string | null,
  };

  try {
    const googleKey = (process.env.NODE_ENV === "production") ? "GOOGLE_MAPS_KEY" : "AIzaSyDbgvgejSNtTI410Z1ihbNGl-0iEpfPl0k";
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=${lang}&key=${googleKey}`;

    const response = await fetch(apiUrl);
    const responseData = await response.json();

    if (responseData && responseData.results && responseData.results.length > 0) {
      const result = responseData.results[0];
      const address = result.formatted_address || '';
      let area = '';
      let zipCode = '';

      for (const component of result.address_components) {
        if (
          component.types.includes("locality") ||
          component.types.includes("political")
        ) {
          area = component.long_name;
        }
        if (component.types.includes("postal_code")) {
          zipCode = component.long_name.replace(/ /g, "");
        }
      }

      mapModel.address = address;
      mapModel.area = area;
      mapModel.zip_code = zipCode;
    }

    return mapModel;
  } catch (error) {
    console.error('Error fetching data from Google Maps API');
    return mapModel;
  }
}

function periodToText(period: number) {
  if (period == 1) {
    return "D";
  } else if (period == 7) {
    return "W";
  } else if (period == 30) {
    return "M";
  } else if (period == 365) {
    return "Y";
  } else {
    return "D";
  }
}

function periodOptionToNumber(option: string) {
  if (option) {
    return option.replace(/[^0-9]/g, "");
  } else {
    return "";
  }
}

function periodOptionToComboText(option: string) {
  if (option) {
    if (parseInt(option) > 1) {
      return option.replace(/[^A-Z]/g, "") == "D"
        ? i18n.t("days")
        : option.replace(/[^A-Z]/g, "") == "W"
        ? i18n.t("weeks")
        : option.replace(/[^A-Z]/g, "") == "M"
        ? i18n.t("months")
        : i18n.t("Years");
    } else {
      return option.replace(/[^A-Z]/g, "") == "D"
        ? i18n.t("day")
        : option.replace(/[^A-Z]/g, "") == "W"
        ? i18n.t("week")
        : option.replace(/[^A-Z]/g, "") == "M"
        ? i18n.t("month")
        : i18n.t("Year");
    }
  } else {
    return "";
  }
}

function periodOptionToComboValue(option: string) {
  if (option) {
    return option.replace(/[^A-Z]/g, "") == "D"
      ? 1
      : option.replace(/[^A-Z]/g, "") == "W"
      ? 7
      : option.replace(/[^A-Z]/g, "") == "M"
      ? 30
      : 365;
  } else {
    return 0;
  }
}

function isNumber(evt: any) {
  evt = evt || window.event;
  const charCode = evt.which || evt.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    evt.preventDefault();
  } else {
    return true;
  }
  return false;
}

function periodOptionToLabel(option: string) {
  if (option) {
    const period_num = option.replace(/[^0-9]/g, "");
    const period_opt = option.replace(/[^A-Z]/g, "");

    if (period_num == "1") {
      return period_opt == "D"
        ? period_num + " " + i18n.t("day")
        : period_opt == "W"
        ? period_num + " " + i18n.t("week")
        : period_opt == "M"
        ? period_num + " " + i18n.t("month")
        : period_num + " " + i18n.t("Year");
    } else {
      return period_opt == "D"
        ? period_num + " " + i18n.t("days")
        : period_opt == "W"
        ? period_num + " " + i18n.t("weeks")
        : period_opt == "M"
        ? period_num + " " + i18n.t("months")
        : period_num + " " + i18n.t("Years");
    }
  } else {
    return "-";
  }
}

function getDurationText(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  let duration = "";

  if (hours > 0) {
    duration += hours + "h";
    if (mins > 0) {
      duration += " ";
    }
  }

  if (mins > 0) {
    duration += mins + "m";
  }

  return duration;
}

function durationOptionByMinutes(durationCombo: any[], minutes: number | string | null | undefined) {
  const normalizedMinutes = Number(minutes);
  if (!normalizedMinutes) {
    return null;
  }
  return durationCombo.find((item: any) => Number(item.dur) === normalizedMinutes) || null;
}

function isGasCompany(info: Record<string, any>) {
  return info.text === "GasCompany";
}

function pagesExportLimit() {
  return 500;
}

function templatesFilenameLanguage() {
  let selectedLanguage = localStorage.getItem("lang") || "el";
  selectedLanguage = selectedLanguage === "gr" ? "el" : selectedLanguage!; // for legacy reasons
  return selectedLanguage.toUpperCase();
}

function mapTileProviders() {
  const tileProviders = [
    {
      name: i18n.t("ground"),
      visible: true,
      attribution:
        '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    },
    {
      name: i18n.t("satellite"),
      visible: false,
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution:
        "Esri, Maxar, Earthstar Geographics, and the GIS User Community",
    },
  ];
  return tileProviders;
}

function timeChargedUnitOption(unit: string) {
  const options: any = {
    hour: { unit: "HOUR", text: i18n.t("hour") },
    day: { unit: "DAY", text: i18n.t("day") },
  };
  if (unit != undefined) {
    return options[unit.toLowerCase()];
  }

  return null;
}

function timeChargedUnitLabels(unit: string) {
  const options: any = {
    'hour': { quantity: i18n.t('hours'), cost: i18n.t('hourCost') },
    'day': { quantity: i18n.t('days'), cost: i18n.t('dayCost') }
  }

  if (unit) {
    return options[unit.toLowerCase()];
  }
  return options['hour'];
}
function itemUnitAbbreviation(unit: string | undefined) {
  const options: Record<string, any> = {
    'pieces': i18n.t('abbrevPieces'),
    'meters': i18n.t('abbrevMeters'),
    'centimeters': i18n.t('abbrevCentimeters'),
    'squaremeters':  i18n.t('abbrevSquareMeters'),

    'millimeters': i18n.t('abbrevMillimeters'),
    'squarecentimeters': i18n.t('abbrevsSuarecentimeters'),
    'squaremillimeters': i18n.t('abbrevSquaremillimeters'),
    'cubicmeters': i18n.t('abbrevCubicmeters'),
    'litres': i18n.t('abbrevLitres'),
    'milliliter': i18n.t('abbrevMilliliter'),
    'tonne': i18n.t('abbrevTonne'),
    'kilogram': i18n.t('abbrevKilogram'),
    'gram': i18n.t('abbrevGram'),
    'milligram': i18n.t('abbrevMilligram'),

  };

  if (unit != undefined) {
    return options[unit.toLowerCase()];
  }

  return options['pieces'];
}

function calculateTimeChargingCost(
  action: Record<string, any>,
  isAppointment = false
) {
  // appointment & charging by the hour
  if (isAppointment && action.unit?.unit?.toLowerCase() === "hour") {
    const endDate = dayjs(action.scheduled_end);
    if (endDate.diff(action.scheduled_start, "hour", true) < 1) {
      action.unit_quantity = 1;
    } else {
      action.unit_quantity = Math.round(
        endDate.diff(action.scheduled_start, "hour", true)
      );
    }
  }

  // appointment & charging by day
  if (isAppointment && action.unit?.unit?.toLowerCase() === "day") {
    const endDate = dayjs(action.scheduled_end);
    if (endDate.diff(action.scheduled_start, "day", true) < 1) {
      action.unit_quantity = 1;
    } else {
      action.unit_quantity = Math.round(
        endDate.diff(action.scheduled_start, "day", true)
      );
    }
  }

  if (!isNaN(Number(action.unit_quantity)) && !isNaN(Number(action.unit_cost))) {
    action.cost = Number(
      (Number(action.unit_quantity) * Number(action.unit_cost)).toFixed(2)
    );
  }
}

function timeChargedUnitLabelsInfo(unit: string, quantity: string | number) {
  if(typeof quantity === 'string') {
    quantity = Number(quantity);
  }

  const singularOptions: any = {
    'hour': i18n.t('hourLowerCase'),
    'day': i18n.t('dayLowerCase')
  };

  const pluralOptions: any = {
    'hour': i18n.t('hoursLowerCase'),
    'day': i18n.t('daysLowerCase')
  };

  if (unit && quantity) {
    if(quantity > 1) {
      return pluralOptions[unit.toLowerCase()];
    }
    return singularOptions[unit.toLowerCase()];
  }
}

function jobItemUnitInfo(item: Record<string, any>) {
  if(item.is_time_charged && item.unit && item.unit_quantity && item.unit_cost !== undefined) {
    return `(${item.unit_quantity} ${timeChargedUnitLabelsInfo(item.unit, item.unit_quantity)} x ${item.unit_cost}€)`
  }
  return '';
}

function itemUnitInfoLabel(item: Record<string, any>) {
  if(item.is_unit_based && item.unit_cost != undefined && item.unit_quantity && item.unit) {
    return `(${item.unit_quantity} ${itemUnitAbbreviation(item.item_unit_text)} x ${item.unit_cost}€)`;
  }
  return '';
}

function clientPhoneRegex() {
  //eslint-disable-next-line
  return /^[\+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[0-9]{1,3}[-\s\.]?[0-9]{3,14}$/im;
}

function getPaymentIcon(paymentTypeId: number) {
  switch (paymentTypeId) {
    case 1:
      return 'mdi-cash';
    case 2:
      return 'mdi-credit-card-outline';
    case 3:
      return 'mdi-bank-outline';
    case 4:
      return 'mdi-checkbook';
    default:
      return 'mdi-cash-sync';
  }
}

function installationPhotoCompressionSettings() {
  return {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };
}

function announcementColor(type: string) {
  const colors: Record<string, string> = {
    info: 'infoAnnouncement',
    warning: 'warningAnnouncement',
    success: 'successAnnouncement',
    error: 'errorAnnouncement',
  };

  return colors[type] ?? 'info';
}

function announcementIcon(type: string) {
  const icons: Record<string, string> = {
    info: 'mdi-information-outline',
    warning: 'mdi-alert-outline',
    success: 'mdi-check-circle-outline',
    error: 'mdi-alert-circle-outline'
  };

  return icons[type] ?? 'information-outline';
}

function deepTrim(obj: any): any {
  if (typeof obj === 'string') return obj.trim();
  if (Array.isArray(obj)) return obj.map(deepTrim);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, deepTrim(v)])
    );
  }
  return obj;
}

async function checkDuplicateContact(contactsArray: { phone: string }[], type: string, currentUser: string | null = null) {

  const module = type === 'client' ? clientsModule : partnersModule;

  const contacts = contactsArray
    .map(obj => obj.phone ? obj.phone.trim() : '')
    .filter(phone => phone !== '' && phone.length >= 4);

  if (!contacts.length) {
    return { hasDuplicates: false, message: '' };
  }

  const resp = await module.checkForDuplicateContacs({ contacts: contacts, current_user: currentUser });

  if (Array.isArray(resp) && resp.length > 0) {
    const message = resp.map(r => `${r.phone} - ${r.contact_names.join(', ')}`).join('\n');
    return { hasDuplicates: true, message };
  }

  return { hasDuplicates: false, message: '' };
}

function getTimeZones() {
  const timezones: string[] = Intl.supportedValuesOf('timeZone');

  const europeanTimezones: string[] = timezones.filter(
    (tz: string) => tz.startsWith('Europe/') || tz === 'Asia/Nicosia'
  );

  const firstOptions = ['Europe/Athens', 'Asia/Nicosia'];

  return [...firstOptions.filter(tz => europeanTimezones.includes(tz)), ...europeanTimezones];
}

function priorityIcon(name: string) {
  const icons: Record<string, string> = {
    'LOWEST': 'mdi-chevron-double-down',
    'LOW': 'mdi-chevron-down',
    'MEDIUM': 'mdi-equal',
    'HIGH': 'mdi-chevron-up',
    'HIGHEST': 'mdi-chevron-double-up'
  };

  return icons[name];
}

function paymentClientsOptions(obj: Record<string, any>) {
  return [
    {
      value: "user",
      icon: obj.installation.info.client_is_business ? "mdi-domain" : "mdi-account",
      label: obj.installation.info.client_is_business
      ? `${i18n.t("tenant")} (${obj.installation.info.client_entity_name} - ${obj.installation.info.client_lastname} ${obj.installation.info.client_firstname})`
      : `${i18n.t("tenant")} (${obj.installation.info.client_lastname} ${obj.installation.info.client_firstname})`
    },
    ...(obj.installation.info.owner_id ? [{
      value: "owner",
      icon: obj.installation.info.owner_is_business ? "mdi-domain" : "mdi-account",
      label: obj.installation.info.owner_is_business
      ? `${i18n.t("owner")} (${obj.installation.info.owner_entity_name} - ${obj.installation.info.owner_lastname} ${obj.installation.info.owner_firstname})`
      : `${i18n.t("owner")} (${obj.installation.info.owner_lastname} ${obj.installation.info.owner_firstname})`
    }] : [])
  ]
}

function canceledAppointmentValues(item: Record<string, any>) {
  const data = {
    msg: '',
    disableTooltip: true
  };

  if(item?.appointment_status_id === 1) {
    if(item.cancellation_reason_id === 5 && item.cancellation_text) {
      data.msg = `${item.cancellation_text}`;
    } else if(item.cancellation_reason_id === 5 && !item.cancellation_text) {
      data.msg = `${i18n.t(item?.cancellation_reason)} ${i18n.t('reasonLowerCase')}`;
    } else {
      data.msg = `${i18n.t(item?.cancellation_reason)}`;
    }

    data.disableTooltip = false;
  }

  return data;
}

function recurrenceTypesOptions(interval = 1) {
  const isPlural = Number(interval) > 1;

  const options = [
    { value: 'daily', text: isPlural ? i18n.t('days') : i18n.t('day') },
    { value: 'weekly', text: isPlural ? i18n.t('weeks') : i18n.t('week') },
    { value: 'monthly', text: isPlural ? i18n.t('months') : i18n.t('month') },
    { value: 'yearly', text: isPlural ? i18n.t('Years') : i18n.t('Year') },
  ];

  return { options, defaultOption: options.find(o => o.value === 'weekly') };
}

function weekDaysOptions() {
  const weekDaysOptions = [
    { value: 'MON', text: i18n.t('monday') },
    { value: 'TUE', text: i18n.t('tuesday') },
    { value: 'WED', text: i18n.t('wednesday') },
    { value: 'THU', text: i18n.t('thursday') },
    { value: 'FRI', text: i18n.t('friday') },
    { value: 'SAT', text: i18n.t('saturday') },
    { value: 'SUN', text: i18n.t('sunday') },
  ];

  return weekDaysOptions;
}

function recurrenceEndTypeOptions() {
  const recurrenceEndTypeOptions = [
    { value: 'until_date', text: i18n.t('untilDate') },
    { value: 'occurrences', text: i18n.t('occurrences') },
    // { value: 'none', text: i18n.t('none') },
  ];

  return recurrenceEndTypeOptions;
}

function recurrenceEndDate() {
  return dayjs().add(18, 'month').format('YYYY-MM-DD');
}

function setRecurrenceDayOnInput(model: Record<string, any>, userChangedDaysOfWeek: boolean) {
  if(userChangedDaysOfWeek) {
    return;
  }

  const day = dayjs(model.scheduled_start, 'YYYY-MM-DD').format('ddd').toUpperCase();

  const validOptions = weekDaysOptions();

  const dayObj = validOptions.find(opt => opt.value === day);

  if(!dayObj) {
    return;
  }

  if(!Array.isArray(model.days_of_week)) {
    model.days_of_week = [];
  }

  model.days_of_week = [dayObj];

  return dayObj;
}

function showRecurrenceDaysOptions(model: Record<string, any>) {
  const type = model.recurrence_type?.value ?? model.recurrence_type;
  if(type) {
    return type === 'weekly';
  }
  return false;
}

function recurrenceOccurMessage(model: Record<string, any>) {
  if (!model.recurrence_type) return '';

  if(model.recurrence_type.value === 'weekly' && (!model.days_of_week || model.days_of_week?.length === 0)) {
    return '';
  }

  const type = model.recurrence_type.value || model.recurrence_type;
  const interval = Number(model.interval) ?? 1;
  const endDate = model.end_date ? dayjs(model.end_date).format('DD/MM/YYYY') : null;

  const weekOptions = weekDaysOptions();

  const getWeekdayLabel = (value: string) => {
    const found = weekOptions.find((w) => w.value === value);
    return found ? String(found.text) : value;
  };

  let msg = `${i18n.t('recurrenceRepeatsMsg')} `;

  switch(type) {
    case 'daily': {
      msg += interval === 1 ? `${i18n.t('recurrenceEveryDayMsg')}` : `${i18n.t('every')} ${interval} ${i18n.t('recurrenceDaysLowerCase')}`;
      break;
    }
    case 'weekly': {
      const days = Array.isArray(model.days_of_week) && model.days_of_week.length ? model.days_of_week
      .map((d: { value: string }) => getWeekdayLabel(d.value)).join(', ') : '';

      if (interval === 1) {
        msg += days ? `${i18n.t('every')} ${days}` : `${i18n.t('everyWeek')}`;
      } else {
        msg += `${i18n.t('every')} ${interval} ${i18n.t('weeksLowerCase')}`;
        if(days) msg += ` ${days}`;
      }
      break;
    }
    case 'monthly': {
      msg += interval === 1 ? `${i18n.t('everyMonth')}` : `${i18n.t('every')} ${interval} ${i18n.t('monthsLowerCase')}`;
      break;
    }
    case 'yearly': {
      msg += interval === 1 ? `${i18n.t('everyYear')}` : `${i18n.t('every')} ${interval} ${i18n.t('yearsLowerCase')}`;
      break;
    }
    default:
      return '';
    }

    if(endDate) {
      msg += ` ${i18n.t('until')} ${endDate}`;
    }

    return msg;
  }

  function recurrenceWeekToDays(model: Record<string, any>) {
    if(!model || !model.recurrence_type) {
      return;
    }

    const type = model.recurrence_type.value;
    const days = model.days_of_week || [];

    if(type !== 'weekly' && Array.isArray(days) && days.length > 0) {
      model.days_of_week = [];
      return;
    }

    if(type === 'weekly' && Array.isArray(days) && days.length === 7) {
      const { options } = recurrenceTypesOptions(model.interval);
      const daily = options.find(o => o.value === 'daily');

      if(daily) {
        model.recurrence_type = daily;
        model.days_of_week = [];
        }
      }
  }

function recurrenceFindObjectDays(daysStr: string) {
  const allDays = weekDaysOptions();
  let result;

  if (daysStr) {
    const daysArr = daysStr.replace(/[{}]/g, '').split(',').map((day: string) => day.trim()).filter(Boolean);
    result = allDays.filter(d => daysArr.includes(d.value));
  }

  return result;
}

const normalizeSearchText = (text: string): string =>
  text.toLocaleLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ς/g, "σ");

function greekSearchFilter(value: unknown, queryText: string, itemText?: string): boolean {
  const text = typeof itemText === "string" ? itemText : value;
  if (text == null || queryText == null || typeof text === "boolean") return false;

  return normalizeSearchText(String(text)).includes(normalizeSearchText(queryText));
}

function assigneeSearchFilter(_item: unknown, queryText: string, itemText: string): boolean {
  return greekSearchFilter(_item, queryText, itemText);
}

function warrantyOptionUnit(option: string) {
  const unit = (option || '').replace(/[^A-Z]/g, '');
  return unit == 'M' ? 'M' : 'Y';
}

function warrantyOptionNumber(option: string) {
  return option ? option.replace(/[^0-9]/g, '') : '';
}

function warrantyValueFromMonths(warranty: any, option: string) {
  if (warranty === null || warranty === undefined || warranty === '') {
    return '';
  }

  const optionNumber = warrantyOptionNumber(option);
  if (optionNumber) {
    return optionNumber;
  }

  const unit = warrantyOptionUnit(option);
  const warrantyToNumber = Number(warranty);
  if (unit == 'Y' && warrantyToNumber % 12 == 0) {
    return warrantyToNumber / 12;
  }

  return warrantyToNumber;
}

function warrantyValueToMonths(warranty: any, option: string) {
  if (warranty === null || warranty === undefined || warranty === "") {
    return '';
  }

  const warrantyToNumber = Number(warranty);
  return warrantyOptionUnit(option) == 'Y' ? warrantyToNumber * 12 : warrantyToNumber;
}

function warrantyPayloadOption(warranty: any, option: string) {
  if (warranty === null || warranty === undefined || warranty === "") {
    return '';
  }

  return `${warranty}${warrantyOptionUnit(option)}`;
}

function warrantyDisplayText(warranty: any, option: string) {
  const value = warrantyValueFromMonths(warranty, option);
  if (value === '') {
    return '-';
  }

  const unit = warrantyOptionUnit(option);
  const label = unit == 'Y' ? Number(value) == 1 ? i18n.t('Year') : i18n.t('Years') : Number(value) == 1 ? i18n.t('month') : i18n.t('months');

  return `${value} ${label}`;
}

function warrantyStatusColor(isWithinWarranty: boolean | null | undefined) {
  if (isWithinWarranty === true) return 'success';
  if (isWithinWarranty === false) return 'error';
  return undefined;
}

// TODO: Remove it if we won't use it
// function warrantyRemainingText(remainingMonths: number | null | undefined) {
//   if (remainingMonths === null || remainingMonths === undefined) {
//     return '';
//   }

//   const absoluteMonths = Math.abs(remainingMonths);
//   const label = absoluteMonths == 1 ? i18n.t('month') : i18n.t('months');

//   if (remainingMonths < 0) {
//     return `${i18n.t('expired')} ${absoluteMonths} ${label} ${i18n.t('ago')}`.trim();
//   }

//   return `${i18n.t('remaining')} ${remainingMonths} ${label}`;
// }

function warrantyUnitOptions(warranty: any) {
  const warrantyNumber = Number(warranty);
  const isPlural = warrantyNumber > 1;

  return [
    { text: isPlural ? i18n.t('months') : i18n.t('month'), value: 'M' },
    { text: isPlural ? i18n.t('Years') : i18n.t('Year'), value: 'Y' },
  ];
}

function primaryInstallationItemText(item: Record<string, any>) {
  const value = item?.primary_items;
  return Array.isArray(value) ? value.join(', ') : value || '';
}


function imageMimeType(filename: string) {
  const extension = filename.split('?')[0].split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    avif: 'image/avif',
    bmp: 'image/bmp',
    gif: 'image/gif',
    heic: 'image/heic',
    heif: 'image/heif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    svg: 'image/svg+xml',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    webp: 'image/webp',
  };

  return extension && mimeTypes[extension] ? mimeTypes[extension] : 'application/octet-stream';
}

export {
  formatDate,
  formatDateCalendar,
  formatOnlyDate,
  calcDuration,
  appointmentStatusToName,
  appointmentStatusToColor,
  addOpacity,
  roundMinutes,
  formatDatesRangeText,
  sortDateRange,
  statusCodeMsg,
  primaryContactExists,
  formatDateWithoutSeconds,
  taskStatusToName,
  taskStatusToColor,
  formatAmount,
  customSearch,
  randomHexColor,
  hexColorRemoveLastTwoDigits,
  sortTableDates,
  isDecimalNumber,
  getOptimalTextColor,
  entryIcon,
  taskColor,
  setGoogleMapsInfoToModel,
  getGoogleMapsInfo,
  periodToText,
  isNumber,
  periodOptionToNumber,
  periodOptionToComboText,
  periodOptionToComboValue,
  periodOptionToLabel,
  getDurationText,
  durationOptionByMinutes,
  isGasCompany,
  pagesExportLimit,
  templatesFilenameLanguage,
  mapTileProviders,
  timeChargedUnitOption,
  timeChargedUnitLabels,
  calculateTimeChargingCost,
  mergedAddressAndArea,
  timeChargedUnitLabelsInfo,
  jobItemUnitInfo,
  itemUnitAbbreviation,
  itemUnitInfoLabel,
  clientPhoneRegex,
  installationPhotoCompressionSettings,
  announcementColor,
  announcementIcon,
  deepTrim,
  checkDuplicateContact,
  getTimeZones,
  priorityIcon,
  paymentClientsOptions,
  canceledAppointmentValues,
  recurrenceTypesOptions,
  weekDaysOptions,
  recurrenceEndTypeOptions,
  recurrenceEndDate,
  setRecurrenceDayOnInput,
  showRecurrenceDaysOptions,
  recurrenceOccurMessage,
  recurrenceWeekToDays,
  recurrenceFindObjectDays,
  greekSearchFilter,
  assigneeSearchFilter,
  warrantyOptionUnit,
  warrantyValueFromMonths,
  warrantyValueToMonths,
  warrantyPayloadOption,
  warrantyDisplayText,
  warrantyStatusColor,
  // warrantyRemainingText,
  warrantyUnitOptions,
  primaryInstallationItemText,
  imageMimeType,
};
